import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  Package,
  AlertTriangle,
  AlertCircle,
  Pencil,
  ClipboardList,
  Settings,
  Trash2,
  Tag,
  Info,
  Lock
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { EstoqueForm } from "@/components/estoque/EstoqueForm";
import { RegistroUsoForm } from "@/components/estoque/RegistroUsoForm";
import { supabase } from "@/lib/supabase";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useUserRole } from "@/hooks/useUserRole";

export const Route = createFileRoute("/estoque")({
  component: EstoquePage,
});

const CATEGORIA_PADRAO = "Geral";
const CATEGORIAS_SEED = ["Agulhas", "Tintas", "Descartáveis", "Higiene", CATEGORIA_PADRAO];

function EstoquePage() {
  const { loading: roleLoading, isTatuador, effectiveStudioId, permissaoEstoque } = useUserRole();
  const semAcesso = isTatuador && permissaoEstoque !== "todos";
  const [activeTab, setActiveTab] = useState("materiais");
  const [items, setItems] = useState<any[]>([]);
  const [agendamentosHoje, setAgendamentosHoje] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isUsoOpen, setIsUsoOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [usingItem, setUsingItem] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [categorias, setCategorias] = useState<any[]>([]);
  const [novaCategoria, setNovaCategoria] = useState("");

  const fetchEstoque = useCallback(async () => {
    if (!effectiveStudioId) return;
    try {
      const { data, error } = await supabase
        .from("estoque")
        .select("*")
        .eq("studio_id", effectiveStudioId)
        .order("nome");

      if (error) throw error;
      setItems(data || []);
    } catch (error: any) {
      console.error("Erro ao buscar estoque:", error);
      toast.error("Erro ao carregar estoque");
    } finally {
      setIsLoading(false);
    }
  }, [effectiveStudioId]);

  const fetchAgendamentosHoje = useCallback(async () => {
    if (!effectiveStudioId) return;
    try {
      const hoje = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("agendamentos")
        .select("id, cliente_nome, horario_inicio")
        .eq("studio_id", effectiveStudioId)
        .eq("data", hoje)
        .eq("tipo", "cliente");

      if (error) throw error;
      setAgendamentosHoje(data || []);
    } catch (error) {
      console.error("Erro ao buscar agendamentos de hoje:", error);
    }
  }, [effectiveStudioId]);

  const fetchCategorias = useCallback(async () => {
    if (!effectiveStudioId) return;
    try {
      let { data, error } = await supabase
        .from("estoque_categorias")
        .select("id, nome")
        .eq("studio_id", effectiveStudioId)
        .order("nome");
      if (error) throw error;

      // Studio novo (ainda sem nenhuma categoria): semeia as padrão uma única vez
      if (!data || data.length === 0) {
        const { error: seedError } = await supabase
          .from("estoque_categorias")
          .insert(CATEGORIAS_SEED.map(nome => ({ studio_id: effectiveStudioId, nome })));
        if (seedError) throw seedError;
        const refetch = await supabase
          .from("estoque_categorias")
          .select("id, nome")
          .eq("studio_id", effectiveStudioId)
          .order("nome");
        data = refetch.data;
      }

      const sorted = [...(data || [])].sort((a, b) =>
        a.nome === CATEGORIA_PADRAO ? 1 : b.nome === CATEGORIA_PADRAO ? -1 : a.nome.localeCompare(b.nome)
      );
      setCategorias(sorted);
    } catch (error) {
      console.error("Erro ao buscar categorias de estoque:", error);
    }
  }, [effectiveStudioId]);

  const handleAddCategoria = async (nome: string) => {
    if (!nome.trim() || !effectiveStudioId) return;
    try {
      const { error } = await supabase
        .from("estoque_categorias")
        .insert({ studio_id: effectiveStudioId, nome: nome.trim() });
      if (error) throw error;
      setNovaCategoria("");
      fetchCategorias();
    } catch (error: any) {
      toast.error("Erro ao adicionar categoria: " + error.message);
    }
  };

  const handleDeleteCategoria = async (categoria: { id: string; nome: string }) => {
    if (categoria.nome === CATEGORIA_PADRAO) {
      toast.error(`A categoria "${CATEGORIA_PADRAO}" é o destino padrão e não pode ser excluída.`);
      return;
    }
    if (!effectiveStudioId) return;
    try {
      // Reatribui os produtos dessa categoria para a categoria padrão antes de excluir
      const { error: reassignError } = await supabase
        .from("estoque")
        .update({ categoria: CATEGORIA_PADRAO })
        .eq("studio_id", effectiveStudioId)
        .eq("categoria", categoria.nome);
      if (reassignError) throw reassignError;

      const { error } = await supabase.from("estoque_categorias").delete().eq("id", categoria.id);
      if (error) throw error;

      toast.success(`Categoria removida. Produtos movidos para "${CATEGORIA_PADRAO}".`);
      fetchCategorias();
      fetchEstoque();
    } catch (error: any) {
      toast.error("Erro ao remover categoria: " + error.message);
    }
  };

  useEffect(() => {
    if (roleLoading || semAcesso) return;
    fetchEstoque();
    fetchAgendamentosHoje();
    fetchCategorias();
  }, [roleLoading, semAcesso, fetchEstoque, fetchAgendamentosHoje, fetchCategorias]);

  // Ativa o Realtime para a tabela estoque
  useRealtimeSubscription("estoque", fetchEstoque);

  const nomesCategorias = categorias.map(c => c.nome);
  const categories = ["Todos", ...nomesCategorias];

  const filteredItems = useMemo(() => {
    return items
      .filter(item => {
        const matchesSearch = item.nome.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === "Todos" || item.categoria === selectedCategory;
        return matchesSearch && matchesCategory;
      })
      .sort((a, b) => {
        const priority = { "Crítico": 0, "Baixo": 1, "Médio": 2, "Alto": 3 };
        return (priority[a.nivel_atual as keyof typeof priority] || 4) - (priority[b.nivel_atual as keyof typeof priority] || 4);
      });
  }, [items, searchTerm, selectedCategory]);

  const stats = useMemo(() => {
    return {
      total: items.length,
      baixo: items.filter(i => i.nivel_atual === "Baixo").length,
      critico: items.filter(i => i.nivel_atual === "Crítico").length
    };
  }, [items]);

  const handleSaveItem = async (data: any) => {
    try {
      if (!effectiveStudioId) throw new Error("Usuário não autenticado");

      const payload: any = {
        nome: data.nome,
        marca: data.marca,
        categoria: data.categoria,
        tipo_controle: data.tipo_controle,
        observacoes: data.observacoes,
      };

      if (data.tipo_controle === "Por unidade") {
        payload.quantidade_atual = data.quantidade_atual;
        payload.quantidade_minima = data.quantidade_minima;
        payload.nivel_atual = null;
        payload.alerta_nivel = null;
      } else {
        payload.nivel_atual = data.nivel_atual;
        payload.alerta_nivel = data.alerta_nivel;
        payload.quantidade_atual = null;
        payload.quantidade_minima = null;
      }

      if (editingItem) {
        const { error } = await supabase
          .from("estoque")
          .update(payload)
          .eq("id", editingItem.id);

        if (error) throw error;
        toast.success("Produto atualizado!");
      } else {
        const { error } = await supabase
          .from("estoque")
          .insert({ ...payload, studio_id: effectiveStudioId });

        if (error) throw error;
        toast.success("Produto cadastrado!");
      }
      setIsFormOpen(false);
      setEditingItem(null);
      fetchEstoque();
    } catch (error: any) {
      console.error("Erro ao salvar produto no estoque:", error);
      toast.error("Erro ao salvar produto: " + error.message);
    }
  };

  const handleConfirmUso = async (data: any) => {
    if (!usingItem) return;
    try {
      if (!effectiveStudioId) throw new Error("Usuário não autenticado");

      let updatePayload: any = {};

      if (usingItem.tipo_controle === "Por unidade") {
        const novaQtd = Math.max(0, (usingItem.quantidade_atual || 0) - Number(data.quantidade_usada));
        const minima = usingItem.quantidade_minima || 0;
        let nivel_atual: string;
        if (novaQtd <= 0) nivel_atual = "Crítico";
        else if (novaQtd < minima) nivel_atual = "Baixo";
        else if (novaQtd < minima * 2) nivel_atual = "Médio";
        else nivel_atual = "Alto";
        updatePayload = { quantidade_atual: novaQtd, nivel_atual };
      } else {
        updatePayload = { nivel_atual: data.nivel_apos_uso };
      }

      const { error } = await supabase
        .from("estoque")
        .update(updatePayload)
        .eq("id", usingItem.id);

      if (error) throw error;

      // Histórico de consumo
      const { error: usoError } = await supabase.from("estoque_uso").insert({
        studio_id: effectiveStudioId,
        produto_id: usingItem.id,
        agendamento_id: data.agendamento_id || null,
        quantidade_usada: data.quantidade_usada ? Number(data.quantidade_usada) : null,
        nivel_apos_uso:
          usingItem.tipo_controle === "Por nível"
            ? data.nivel_apos_uso
            : updatePayload.nivel_atual || null,
        observacoes: data.observacoes || null,
      });
      if (usoError) console.error("Erro ao gravar histórico de uso:", usoError);

      toast.success(`Uso registrado para ${usingItem.nome}`);
      setIsUsoOpen(false);
      setUsingItem(null);
      fetchEstoque();
    } catch (error: any) {
      console.error("Erro ao registrar uso:", error);
      toast.error("Erro ao registrar uso: " + error.message);
    }
  };

  const getNivelColor = (nivel: string) => {
    switch (nivel) {
      case "Alto": return "text-[#10B981] bg-[#10B981]/10";
      case "Médio": return "text-[#F59E0B] bg-[#F59E0B]/10";
      case "Baixo": return "text-[#F97316] bg-[#F97316]/10";
      case "Crítico": return "text-primary bg-primary/10";
      default: return "text-muted-foreground bg-zinc-500/10";
    }
  };

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case "Tintas": return "bg-purple-500/10 text-purple-400 border-purple-500/20";
      case "Agulhas": return "bg-blue-500/10 text-blue-400 border-blue-500/20";
      case "Descartáveis": return "bg-orange-500/10 text-orange-400 border-orange-500/20";
      case "Higiene": return "bg-teal-500/10 text-teal-400 border-teal-500/20";
      default: return "bg-zinc-500/10 text-muted-foreground border-zinc-500/20";
    }
  };

  if (!roleLoading && semAcesso) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center space-y-3">
        <div className="size-14 rounded-2xl bg-muted/50 flex items-center justify-center text-muted-foreground">
          <Lock size={24} />
        </div>
        <h2 className="text-xl font-semibold text-foreground">Sem acesso ao estoque</h2>
        <p className="text-muted-foreground max-w-sm">
          O dono do estúdio não liberou a visualização do estoque para o seu usuário.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 md:pb-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground">Estoque</h1>
          <p className="text-muted-foreground">Controle seus produtos e insumos</p>
        </div>

        {activeTab === "materiais" && (
          <Button
            onClick={() => {
              setEditingItem(null);
              setIsFormOpen(true);
            }}
            className="bg-primary hover:bg-primary/90 text-white font-semibold h-11 w-full md:w-auto"
          >
            <Plus className="mr-2 size-4" />
            Novo Produto
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-card border border-border h-11 p-1 mb-6">
          <TabsTrigger value="materiais" className="data-[state=active]:bg-primary data-[state=active]:text-white font-semibold px-6">Lista de Materiais</TabsTrigger>
          <TabsTrigger value="configuracoes" className="data-[state=active]:bg-primary data-[state=active]:text-white font-semibold px-6">
            <Settings className="mr-2 size-4" />
            Configurações
          </TabsTrigger>
        </TabsList>

        <TabsContent value="materiais" className="mt-0 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-card border-border overflow-hidden">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="size-12 rounded-xl bg-white/5 flex items-center justify-center text-foreground">
                  <Package size={24} />
                </div>
                <div>
                  <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-widest">Produtos Cadastrados</p>
                  <h3 className="text-2xl font-semibold text-foreground">{stats.total}</h3>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border overflow-hidden">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="size-12 rounded-xl bg-[#F97316]/10 flex items-center justify-center text-[#F97316]">
                  <AlertTriangle size={24} />
                </div>
                <div>
                  <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-widest">Nível Baixo</p>
                  <h3 className="text-2xl font-semibold text-[#F97316] flex items-center gap-2">
                    {stats.baixo}
                    <Badge className="bg-[#F97316] text-foreground font-semibold h-5 px-1.5">{stats.baixo}</Badge>
                  </h3>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border overflow-hidden">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="size-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <AlertCircle size={24} />
                </div>
                <div>
                  <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-widest">Nível Crítico</p>
                  <h3 className="text-2xl font-semibold text-primary flex items-center gap-2">
                    {stats.critico}
                    <Badge className="bg-primary text-white font-semibold h-5 px-1.5">{stats.critico}</Badge>
                  </h3>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome do produto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-card border-border text-foreground h-11 focus-visible:ring-primary"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 custom-scrollbar">
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant="ghost"
                  onClick={() => setSelectedCategory(cat)}
                  className={cn(
                    "h-11 px-6 font-medium text-xs uppercase tracking-widest rounded-lg border transition-all",
                    selectedCategory === cat
                      ? "bg-primary text-white border-primary"
                      : "bg-card text-muted-foreground border-border hover:bg-white/5"
                  )}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map(item => (
              <Card
                key={item.id}
                className={cn(
                  "bg-card border-border transition-all hover:border-white/10 group overflow-hidden",
                  item.nivel_atual === "Crítico" && "border-primary animate-pulse ring-1 ring-primary/30",
                  item.nivel_atual === "Baixo" && "border-[#F97316]"
                )}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={cn("uppercase text-[8px] font-medium h-4", getCategoryColor(item.categoria))}>
                          {item.categoria}
                        </Badge>
                        {item.marca && <span className="text-[10px] text-muted-foreground font-semibold">{item.marca}</span>}
                      </div>
                      <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">{item.nome}</h3>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingItem(item);
                        setIsFormOpen(true);
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Pencil size={16} />
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest">Nível Atual</p>
                      <div className={cn("text-xs font-medium uppercase flex items-center gap-1", getNivelColor(item.nivel_atual).split(' ')[0])}>
                        <div className={cn("size-2 rounded-full", getNivelColor(item.nivel_atual).split(' ')[0].replace('text-', 'bg-'))}></div>
                        {item.nivel_atual}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest">Em Estoque</p>
                      <p className="text-lg font-semibold text-foreground">
                        {item.tipo_controle === "Por unidade" ? `${item.quantidade_atual} un` : item.nivel_atual}
                      </p>
                    </div>
                  </div>

                  <Button
                    onClick={() => {
                      setUsingItem(item);
                      setIsUsoOpen(true);
                    }}
                    className="w-full bg-white/5 hover:bg-primary text-foreground hover:text-white font-semibold h-10 border border-border hover:border-primary transition-all"
                  >
                    <ClipboardList className="mr-2 size-4" />
                    Registrar Uso
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="configuracoes" className="mt-0">
          <Card className="bg-card border-border overflow-hidden max-w-2xl">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-start gap-3">
                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <Tag size={20} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground uppercase tracking-wide">Gestão de Categorias</h3>
                  <p className="text-sm text-muted-foreground">Defina como seus materiais serão agrupados.</p>
                </div>
              </div>

              <div className="border-t border-border" />

              <div className="flex gap-2">
                <Input
                  value={novaCategoria}
                  onChange={(e) => setNovaCategoria(e.target.value)}
                  placeholder="Nova categoria..."
                  className="bg-background border-border h-11"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddCategoria(novaCategoria); } }}
                />
                <Button className="h-11 bg-primary hover:bg-primary/90 text-white font-semibold px-6" onClick={() => handleAddCategoria(novaCategoria)}>
                  <Plus className="mr-2 size-4" />
                  Adicionar
                </Button>
              </div>

              <div className="space-y-2">
                {categorias.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-4 rounded-xl bg-background border border-border">
                    <div className="flex items-center gap-3">
                      <div className="size-2 rounded-full bg-primary/60" />
                      <span className="font-semibold text-foreground">{cat.nome}</span>
                    </div>
                    {cat.nome === CATEGORIA_PADRAO ? (
                      <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                        <Lock size={11} /> Padrão
                      </span>
                    ) : (
                      <button
                        onClick={() => handleDeleteCategoria(cat)}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 text-xs">
                <Info size={14} className="shrink-0 mt-0.5" />
                <span>Ao excluir uma categoria, todos os itens associados a ela serão automaticamente movidos para a categoria "{CATEGORIA_PADRAO}".</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="p-0 border-none bg-transparent max-w-lg shadow-none">
          <EstoqueForm
            title={editingItem ? "Editar Produto" : "Novo Produto"}
            initialData={editingItem}
            categoriasDisponiveis={nomesCategorias}
            onSubmit={handleSaveItem}
            onCancel={() => setIsFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isUsoOpen} onOpenChange={setIsUsoOpen}>
        <DialogContent className="p-0 border-none bg-transparent max-w-lg shadow-none">
          {usingItem && (
            <RegistroUsoForm
              produto={usingItem}
              agendamentos={agendamentosHoje}
              onSubmit={handleConfirmUso}
              onCancel={() => setIsUsoOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
